/* eslint-disable prettier/prettier */
import {
  useLoanDataFieldSelectorHook
} from '@frontend/pricing-search';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../../../common/utils/api';
import {
  miRequestActions,
  selectEncompassClientId,
  selectEncompassUserId,
  selectInstanceId,
} from '../../../../store/miRequest';
import { getEncompassClientAndUserId, shouldCalculateAPR } from '../../../../store/miRequest/utils/helpers';
import { buildMIDRQPayload } from '../../../../store/miRequest/utils/buildMIPayload';
import { buildAPRPayload } from '../../../../store/miRequest/utils/buildAPRPayload';
import { computeMIAmount, DEFAULT_MI_ERROR, DisplayEstimatedMI, extractAPRFromResponse, MIError, MIStore } from './MIStore';
import { showMIErrorToast } from './notifications';
import Common from '../../../../common/services/Common';

export const getMIKey = (
  rateDataID: number | undefined,
  programID: number | undefined,
) => `${programID}-${rateDataID}`;

export const useGetMI = () => {
  const dispatch = useDispatch();
  const loanData = useLoanDataFieldSelectorHook() as any;
  const encompassClientId = useSelector(selectEncompassClientId);
  const encompassUserId = useSelector(selectEncompassUserId);
  const instanceId = useSelector(selectInstanceId);
  const loanProgramsData = useSelector(({ EPPS }: any) => EPPS.loanPrograms);
  const clientSettings = useSelector(({ eppsData }: any) => eppsData?.lookups?.data?.clientSettings);
  const loanTerms = useSelector(({ eppsData }: any) => eppsData?.lookups?.raw?.loanTerms ?? []);

  const loanDataRef = useRef(loanData);
  loanDataRef.current = loanData;
  const encompassClientIdRef = useRef(encompassClientId);
  encompassClientIdRef.current = encompassClientId;
  const encompassUserIdRef = useRef(encompassUserId);
  encompassUserIdRef.current = encompassUserId;
  const instanceIdRef = useRef(instanceId);
  instanceIdRef.current = instanceId;
  const loanProgramsRef = useRef(loanProgramsData);
  loanProgramsRef.current = loanProgramsData;
  const clientSettingsRef = useRef(clientSettings);
  clientSettingsRef.current = clientSettings;
  const loanTermsRef = useRef(loanTerms);
  loanTermsRef.current = loanTerms;

  const activeControllersRef = useRef<Map<string, AbortController>>(new Map());
  // we will use this to prevent race conditions when multiple calls to getEncompassClientId are made at the same time
  const clientIdFetchRef = useRef<Promise<{ clientId: string; encompassUserId: string }> | null>(null);

  const store = useMemo(() => new MIStore(), []);

  useEffect(() => {
    return () => {
      activeControllersRef.current.forEach((controller) => controller.abort());
      activeControllersRef.current.clear();
      store.dispose();
    };
  }, []);

  const handleGetMI = useCallback(
    async ({
      rateDataID,
      programID,
      rateData,
      displayEstimatedMI,
      selectedMiType,
    }: {
      rateDataID: number;
      programID: number;
      rateData?: any;
      displayEstimatedMI?: string;
      selectedMiType: string;
    }) => {
      const key = getMIKey(rateDataID, programID);
      store.updateRowState(key, { status: 'loading' });
      const controller = new AbortController();
      activeControllersRef.current.set(key, controller);

      let clientId = '';
      let userId = '';
      let miAmount: number | null = null;

      // Phase 1: MI rate quote
      try {
        clientId = encompassClientIdRef.current ?? '';
        userId = encompassUserIdRef.current ?? '';
        if (!clientId) {
          if (!clientIdFetchRef.current) {
            clientIdFetchRef.current = getEncompassClientAndUserId()
              .then((result) => {
                dispatch(miRequestActions.setEncompassClientId(result.clientId));
                dispatch(miRequestActions.setEncompassUserId(result.encompassUserId));
                encompassClientIdRef.current = result.clientId;
                encompassUserIdRef.current = result.encompassUserId;
                return result;
              })
              .finally(() => {
                clientIdFetchRef.current = null;
              });
          }
          ({ clientId, encompassUserId: userId } = await clientIdFetchRef.current);
        }
        const result = await api.getMIDetailedRateQuote({
          body: await buildMIDRQPayload({
            loanData: loanDataRef.current,
            rateData,
            loanTerms: loanTermsRef.current,
            encompassClientId: clientId,
            encompassUserId: userId,
          }),
          signal: controller.signal,
        });
        miAmount = computeMIAmount(result, (displayEstimatedMI ?? '') as DisplayEstimatedMI, selectedMiType);
        if (miAmount == null) {
          store.updateRowState(key, { status: 'complete' });
          return;
        }
        if (!shouldCalculateAPR(clientSettingsRef.current)) {
          store.updateRowState(key, { status: 'complete', miAmount });
          return;
        }
        store.updateRowState(key, { status: 'loading-apr', miAmount });
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          store.updateRowState(key, { status: 'idle' });
        } else {
          const apiError = error?.apiError;
          const miError: MIError = {
            toastMessage: apiError?.summary ?? DEFAULT_MI_ERROR.toastMessage,
            tooltipMessage: apiError?.code ?? DEFAULT_MI_ERROR.tooltipMessage,
          };
          store.updateRowState(key, { status: 'error', error, miError });
          showMIErrorToast(miError.toastMessage);
          throw error;
        }
        return;
      } finally {
        activeControllersRef.current.delete(key);
      }

      const aprController = new AbortController();
      activeControllersRef.current.set(key, aprController);

      try {
        const aprPayload = buildAPRPayload({
          loanData: loanDataRef.current,
          loanProgramsData: loanProgramsRef.current,
          rateData,
          rateDataID,
          miAmount,
          instanceId: instanceIdRef.current,
        });
        // we will not make APR call if aprPayload is null,
        // because it means that the program does not have a
        // loanTemplatePath, loanTemplateType, or loanTemplate, which are required for APR calculation
        if (aprPayload === null) {
          store.updateRowState(key, { status: 'complete' });
          return;
        }
        const aprResult = await api.calculateAPR({
          body: aprPayload,
          signal: aprController.signal,
          originId: (await Common.getOriginId()) ?? '',
        });
        const aprValue = extractAPRFromResponse(aprResult, rateDataID);
        store.updateRowState(key, { status: 'complete', aprValue });
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          store.updateRowState(key, { status: 'idle' });
        } else {
          // APR error is non-fatal: MI value is already displayed, APR falls back 'Use Encompass'
          store.updateRowState(key, { status: 'complete', aprValue: 0 });
        }
      } finally {
        activeControllersRef.current.delete(key);
      }
    },
    [],
  );

  return { store, handleGetMI };
};
